import { Alert, Button, Drawer, Form, Segmented } from 'antd';
import { useState } from 'react';
import { CustomerSearchSelect } from '../../customers/public/CustomerSearchSelect';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { useSubmission } from '../../../shared/hooks/use-submission';
import { useToast } from '../../../shared/hooks/use-toast';
import { createRateCard } from '../rate-cards.api';

type CardKind = 'customer' | 'standard';

interface CreateRateCardDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** A customer's card, or the one standard card spot business falls back to; the server refuses a second of either. */
export function CreateRateCardDrawer({ open, onClose, onCreated }: CreateRateCardDrawerProps) {
  const [form] = Form.useForm<{ customerId?: string }>();
  const [kind, setKind] = useState<CardKind>('customer');
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  async function create({ customerId }: { customerId?: string }) {
    const created = await submit(
      async () => {
        await createRateCard({ customerId: kind === 'customer' ? (customerId ?? null) : null });
        return true;
      },
      (err) => apiErrorMessage(err) ?? 'Could not create the rate card — check the fields and try again.',
    );
    if (!created) return;
    toast.success(kind === 'customer' ? 'Rate card created' : 'Standard rate card created');
    form.resetFields();
    onCreated();
    onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create rate card" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={create} disabled={submitting}>
        <Segmented
          className="mb-5"
          value={kind}
          onChange={(value) => {
            clearError();
            setKind(value as CardKind);
          }}
          options={[
            { value: 'customer', label: 'Customer card' },
            { value: 'standard', label: 'Standard card' },
          ]}
        />
        {kind === 'customer' && (
          <Form.Item name="customerId" label="Customer" rules={[{ required: true, message: 'Choose a customer' }]}>
            <CustomerSearchSelect />
          </Form.Item>
        )}
        <Button type="primary" htmlType="submit" loading={submitting}>
          Create rate card
        </Button>
      </Form>
    </Drawer>
  );
}
